import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { AddToFeedback } from "../../API/Feedback/AddFeedBack";
import { getUserProfile } from "../../API/UserProfileApi/GetUserProfile";
import ReactGA from "react-ga4";
import { useLocalization } from "../Context/MultiLanguageContext";

interface UserProfile {
  userName: string;
  email: string;
  phoneNumber: string;
}

const MAX_FEEDBACK_LENGTH = 300;

const FeedBack: React.FC<{ updateUserName: (newUserName: string) => void }> = ({ updateUserName }) => {
  const navigate = useNavigate();
  const { t } = useLocalization();

  const [feedbackContent, setFeedbackContent] = useState('');
  const [subject, setSubject] = useState('');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });
  }, []);

  useEffect(() => {
    const hasChanges = feedbackContent !== '' || subject !== '';
    setIsFormChanged(hasChanges);
  }, [feedbackContent, subject]);

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  const fetchAndHandleUserProfile = async () => {
    try {
      const data = await getUserProfile(t);
      setUserProfile(data);
      setUserName(data.userName);
      updateUserName(data.userName);
      navigate('/profile', {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      toast.error(t("sessionExpired"));
      navigate('/home');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userName');

    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error(t("sessionExpired"));
      navigate('/home');
      return;
    }

    // ✅ Validation
    if (!subject.trim()) {
      toast.error(t("subjectRequired") || "Subject is required");
      return;
    }

    if (!feedbackContent.trim()) {
      toast.error(t("feedbackRequired") || "Feedback cannot be empty");
      return;
    }

    if (feedbackContent.length > MAX_FEEDBACK_LENGTH) {
      toast.error(`Maximum ${MAX_FEEDBACK_LENGTH} characters allowed`);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await AddToFeedback(feedbackContent, subject, token);

      if (response) {
        ReactGA.event({
          category: "Feedback",
          action: "Submitted Feedback",
          label: subject,
        });

        toast.success(t("feedbackSuccess") || "Feedback submitted successfully");

        setFeedbackContent('');
        setSubject('');
        setIsFormChanged(false);
      }
    } catch (error: any) {
      toast.error(error.message || t("feedbackError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isFormChanged && !isSubmitting) {
      handleFeedbackSubmit(e as unknown as React.FormEvent);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 overflow-hidden">
      <Header
        userName={userName}
        fetchUserProfile={fetchAndHandleUserProfile}
        handleLogout={handleLogout}
      />

      <div className="flex-1 p-4 md:p-6">
        <h1 className="text-xl font-bold mb-6 text-center">
          {t("submitFeedback")}
        </h1>

        <div className="w-full mx-auto p-4 md:p-8 bg-white rounded-lg">
          <form className="space-y-4" onSubmit={handleFeedbackSubmit}>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                {t("subject")}
              </label>
              <input
                id="subject"
                type="text"
                value={subject}
                placeholder={t("feedbackSubject")}
                onChange={(e) => setSubject(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Feedback */}
            <div>
              <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                {t("feedback")}
              </label>
              <textarea
                id="feedback"
                value={feedbackContent}
                onChange={(e) => {
                  const value = e.target.value;

                  // ✅ Restrict input to 300
                  if (value.length <= MAX_FEEDBACK_LENGTH) {
                    setFeedbackContent(value);
                  }
                }}
                onKeyDown={handleKeyDown}
                rows={5}
                className={`w-full px-4 py-2 border rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${feedbackContent.length >= MAX_FEEDBACK_LENGTH ? 'border-red-500' : 'border-gray-200'}`}
                placeholder={t("feedbackContent")}
              />

              {/* ✅ Character Counter */}
              <p className={`text-right text-sm mt-1 ${
                feedbackContent.length >= MAX_FEEDBACK_LENGTH ? 'text-red-500' : 'text-gray-500'
              }`}>
                {feedbackContent.length}/{MAX_FEEDBACK_LENGTH}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-end gap-4 mt-4">
              <button
                id="submit"
                disabled={
                  !isFormChanged ||
                  isSubmitting ||
                  feedbackContent.length > MAX_FEEDBACK_LENGTH ||
                  !feedbackContent.trim() ||
                  !subject.trim()
                }
                className={`py-2 px-4 text-white rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors
                  ${isFormChanged && !isSubmitting && feedbackContent.length <= MAX_FEEDBACK_LENGTH && feedbackContent.trim() && subject.trim()
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-400 cursor-not-allowed'}`}
              >
                {isSubmitting ? t("submitting") : t("submit")}
              </button>

              <button
                type="button"
                onClick={() => navigate('/more')}
                className="py-2 px-4 bg-gray-300 text-gray-800 rounded-md shadow hover:bg-gray-400 focus:outline-none transition-colors"
              >
                {t("cancel")}
              </button>
            </div>

          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FeedBack;